import { useState, useEffect, useCallback } from 'react';
import { FaShoppingCart, FaPlusCircle, FaCheckCircle, FaMapMarkerAlt, FaRegEdit, FaCreditCard } from 'react-icons/fa';
import { MdRemoveCircle, MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { IoIosAddCircle, IoIosArrowBack } from 'react-icons/io';
import { AiOutlineDelete } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import { updateQuantity, removeFromCart, removeProductFromCart, clearUserCart } from '../redux/cartSlice';
import { apiFetch, getProductById, getCurrentUser, fetchPincodeIndia } from '../api';
import './Checkout.css';
import ProductModal from '../components/ProductModal';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import ResultScreen from '../components/ResultScreen';
import { Modal, Button } from 'react-bootstrap';

const CheckoutPage = () => {
  // Redux and Router hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Redux state and local storage
  const cart = useSelector(state => state.cart);
  const token = localStorage.getItem('token');
  
  // Helper to get user ID
  const getUserIdFrom = useCallback((u) => (u ? (u.id || u._id) : null), []);
  // initial user state from localStorage
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const [userId, setUserId] = useState(() => getUserIdFrom(JSON.parse(localStorage.getItem('user') || 'null')));
  const [isLoading, setIsLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressFormData, setAddressFormData] = useState({});
  const [addressStep, setAddressStep] = useState('details');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isFetchingPin, setIsFetchingPin] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [activeStep, setActiveStep] = useState(1); 
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  // Fetch user addresses on mount
  useEffect(() => {
    const fetchUserAddresses = async () => {
      if (!token) {
        setRedirectingToLogin(true);
        navigate('/login');
        return;
      }

      if (!userId) {
        try {
          // Fetch current user if userId is not available
          const me = await getCurrentUser();
          if (me && me.success && me.data) {
            const newUser = me.data;
            localStorage.setItem('user', JSON.stringify(newUser));
            setUser(newUser);
            setUserId(getUserIdFrom(newUser));
          }
        } catch (err) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setRedirectingToLogin(true);
          navigate('/login');
          return;
        }
      }
      // fetch addresses
      try {
        const res = await apiFetch(`/api/addresses/user/${userId}`);
        if (res.success && res.data.length > 0) {
          setAddresses(res.data);
          const defaultAddress = res.data.find(addr => addr.is_default) || res.data[0];
          setSelectedAddress(defaultAddress);
          setShowAddressForm(false);
        } else {
          setShowAddressForm(true);
        }
      } catch (error) {
        toast.error("Could not fetch addresses.");
        setShowAddressForm(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserAddresses();
  }, [userId, navigate, token, getUserIdFrom]);
  // remove item from cart with confirmation modal
  const removeItem = (product) => {
    setItemToRemove(product);
    setShowConfirm(true);
  };

  // Confirm item removal
  const handleConfirmRemove = async () => {
    if (itemToRemove) {
      try {
        // remove item logic
        if (localStorage.getItem('token')) {
          await dispatch(removeProductFromCart(itemToRemove));
        } else {
          // remove item logic for guest users
          dispatch(removeFromCart(itemToRemove.id));
          window.dispatchEvent(new CustomEvent('cartNotification', { detail: { type: 'cart', subType: 'remove', message: 'Removed from cart', item: itemToRemove }}));
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to remove item.');
      }
    }
    // Close confirmation modal and reset item to remove
    setShowConfirm(false);
    setItemToRemove(null);
  };

  // update quantity handler
  const updateQuantityHandler = useCallback(async (id, newQuantity) => {
    if (newQuantity < 1) {
      if (localStorage.getItem('token')) {
        const product = cart.items.find(item => item.id === id);
        if (product) {
          await dispatch(removeProductFromCart(product));
        }
      } else {
        dispatch(removeFromCart(id));
      }
      return;
    }
    dispatch(updateQuantity({ id, quantity: newQuantity }));
  }, [cart.items, dispatch]);

  const formatCurrency = (amount) => `₹${amount.toFixed(2)}`;
  const cartTotal = Number(cart.total || 0);
  const displayItemCount = cart.itemCount || 0;

  // handle address form input changes
  const handleAddressFormChange = (e) => {
    setAddressFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // fetch pincode and auto-fill city/state
  const fetchPincodeAndFill = async (pincode) => {
    if (!pincode || !/^[0-9]{6}$/.test(String(pincode))) {
      toast.error('Enter a valid PIN code (6 digits)');
      return;
    }
    setIsFetchingPin(true);
    try {
      // fetch pincode info
      const info = await fetchPincodeIndia(pincode);
      setAddressFormData(prev => ({ ...prev, city: info.district || prev.city, state: info.state || prev.state, country: 'India' }));
      toast.success('Auto-filled city/state from PIN');
      setAddressStep('details');
    } catch (err) {
      toast.error(err.message || 'Could not fetch PIN details');
    } finally {
      setIsFetchingPin(false);
    }
  };

  // handle save address
  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    // determine url and method
    const url = addressFormData._id ? `/api/addresses/update/${addressFormData._id}` : '/api/addresses/create';
    const method = addressFormData._id ? 'PUT' : 'POST';
    try {
      // validation
      const validate = (data) => {
        if (!data.addressLine1 || !/\d/.test(data.addressLine1)) return 'Address Line 1 must include house/building number';
        if (!data.pincode || !/^[0-9]{6}$/.test(String(data.pincode))) return 'Pincode must be 6 digits';
        const containsNumber = s => /\d/.test(String(s || ''));
        if (containsNumber(data.city)) return 'City must not contain numbers';
        if (containsNumber(data.state)) return 'State must not contain numbers';
        if (containsNumber(data.country)) return 'Country must not contain numbers';
        return null;
      };
      // validation
      const validationError = validate(addressFormData);
      if (validationError) {
        toast.error(validationError);
        setIsProcessing(false);
        return;
      }
      // prepare payload
      const payload = {
        user_id: userId,
        street: addressFormData.addressLine1 || addressFormData.street || null,
        city: addressFormData.city || null,
        state: addressFormData.state || null,
        pincode: addressFormData.pincode || null,
        country: addressFormData.country || null,
        phone: addressFormData.phone || null,
        is_default: !!addressFormData.is_default
      };
      // API call to save address
      const res = await apiFetch(url, { method, body: payload });
      if (!res.success) throw new Error(res.message);
      toast.success(res.message || "Address saved successfully!");
      // refresh addresses
      const updatedAddressesRes = await apiFetch(`/api/addresses/user/${userId}`);
      if (updatedAddressesRes.success) {
        // update addresses state with refreshed data
        setAddresses(updatedAddressesRes.data);
        const newAddress = updatedAddressesRes.data.find(addr => addr._id === (res.data?._id || addressFormData._id)) || updatedAddressesRes.data[0];
        setSelectedAddress(newAddress);
        setShowAddressForm(false);
      }
    } catch (error) {
      toast.error(error.message || "Failed to save address.");
    } finally {
      setIsProcessing(false);
    }
  };
  // handle proceed to payment
  const handleProceedToPayment = async () => {
    if (!selectedAddress) {
      toast.error("Please select or add a shipping address.");
      return;
    }
    // Checks terms accepted or nott
    if (!termsAccepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy.");
      return;
    }
    setActiveStep(2);
  };
  // handle payment
  const handlePayment = async () => {
    if (!selectedAddress) {
      toast.error("Please select or add a shipping address.");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please accept the Terms & Conditions and Privacy Policy.");
      return;
    }
    setIsProcessing(true);
    let ourOrderId = null;

    // ------------------------- //
    // Razorpay payment integration
    try {
      const generateObjectId = () => {
        let ts = Math.floor(new Date().getTime() / 1000).toString(16);
        while (ts.length < 8) ts = '0' + ts;
        let rand = '';
        for (let i = 0; i < 16; i++) rand += Math.floor(Math.random() * 16).toString(16);
        return ts + rand;
      };

      const tempOrderId = generateObjectId();
      // Create order items first
      const createdOrderItemPromises = cart.items.map(item =>
        apiFetch('/api/order-items/create', {
          method: 'POST',
          body: {
            order: tempOrderId,
            product: item.id,
            quantity: item.quantity,
            price: item.price,
            color: item.selectedColor || null,
            size: item.selectedSize || null
          }
        }).then(res => res.data || res)
      );
      // Wait for all order items to be created
      const createdOrderItems = await Promise.all(createdOrderItemPromises);
      const orderItemIds = createdOrderItems.map(i => i._id || i.data?._id || i.data);

      // Create the main order
      const orderPayload = {
        user: userId,
        orderItems: orderItemIds,
        address: selectedAddress._id,
        total_amount: Number((cart.total || 0).toFixed(2)),
        tax_amount: 0
      };
      // Create internal order
      const internalOrder = await apiFetch('/api/orders/create', { method: 'POST', body: orderPayload });
      ourOrderId = internalOrder?.order?._id;
      if (!ourOrderId) {
        throw new Error("Could not retrieve order ID after creation.");
      }
      // Create Razorpay order
      const razorpayOrderData = await apiFetch('/api/payments/create', {
        method: 'POST',
        body: { orderId: ourOrderId }
      });
      // Razorpay options
      const key_id = process.env.REACT_APP_RAZORPAY_KEY_ID;
      const { id: razorpayOrderId, amount } = razorpayOrderData.razorpayOrder;

      if (!key_id) {
        throw new Error("Razorpay Key ID is not configured in the .env file.");
      }

      // Razorpay payment options
      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "Label Aadvi",
        description: `Order ID: ${ourOrderId}`,
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            const verifyData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId: ourOrderId
            };

            // Verify payment via Vercel
            const MAIL_URL = process.env.REACT_MAIL || "https://server-e.vercel.app";
            console.log('Verifying payment on Vercel:', MAIL_URL);
            await apiFetch('/api/payments/verify', {
              method: 'POST',
              body: verifyData,
              baseUrl: MAIL_URL
            });
            // Clear cart locally and server-side
            await dispatch(clearUserCart());
            setPaymentResult('success');
            // Redirect to order tracking after a short delay
            setTimeout(() => {
                setPaymentResult(null);
                if (ourOrderId) {
                  navigate(`/ordertracking/${ourOrderId}`);
                } else {
                  navigate('/orderhistory');
                }
            }, 3000);

          } catch (error) {
            // Handle payment verification failure
            console.error('❌ Payment verification failed:', error);
            toast.error("Payment verification failed. Please contact support.");
            setPaymentResult('failed');
            setTimeout(() => {
                setPaymentResult(null);
                setIsProcessing(false);
            }, 7000);
          }
        },
        modal: {
            ondismiss: function() {
                setIsProcessing(false);
            }
        },
        // Prefill user information for Razorpay checkout
        prefill: {
          name: `${user.firstName || ''} ${user.lastName || ''}`,
          email: user.email,
          contact: user.phone || selectedAddress.phone
        },
        notes: {
          address: `${selectedAddress.street || selectedAddress.addressLine1}, ${selectedAddress.city}`
        },
        theme: {
          color: "#82287A"
        }
      };
      
      //------------------------------------------- //
      // Open Razorpay checkout
      
      const rzp1 = new window.Razorpay(options);
      rzp1.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setPaymentResult('failed');
        setTimeout(() => {
            setPaymentResult(null);
            setIsProcessing(false);
        }, 3000);
      });
      rzp1.open();
    } catch (error) {
      toast.error("An error occurred: " + error.message);
      setIsProcessing(false);
    }
  };

  return (
    <>
      {redirectingToLogin && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.85)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <Spinner />
          <p style={{ marginTop: 12, color: '#333' }}>Redirecting to Login…</p>
        </div>
      )}
      {paymentResult && <ResultScreen status={paymentResult} />}
      <div className="checkout-page">
        {/* Header with Progress Steps */}
        <div className="checkout-header">
          <div className="container">
            <div className="checkout-progress">
              <div className={`progress-step ${activeStep >= 1 ? 'active' : ''}`}>
                <div className="step-icon">1</div>
                <span className="step-label">Shipping Address</span>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${activeStep >= 2 ? 'active' : ''}`}>
                <div className="step-icon">2</div>
                <span className="step-label">Payment</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="checkout-grid">
            {/* Left Column - Order Summary */}
            <div className="order-summary-panel">
              <div className="panel-header">
                <h3><FaShoppingCart /> Order Summary</h3>
                <span className="item-count">{displayItemCount} items</span>
              </div>

              {cart.items.length === 0 ? (
                <div className="empty-cart">
                  <FaShoppingCart size={48} className="empty-icon" />
                  <p>Your cart is empty</p>
                  <Link to="/products" className="btn btn-primary">Continue Shopping</Link>
                </div>
              ) : (
                <>
                  <div className="cart-items-list">
                    {cart.items.map((item, index) => (
                      <div key={item.cartItemId || `${item.id}-${item.variantId || index}`} className="cart-item" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
                        <div className="item-image-container" style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                          <img 
                            src={item.image} 
                            alt={item.title} 
                            className="item-image"
                            style={{ width: '180px', height: '180px', objectFit: 'contain', borderRadius: '8px' }}
                            onClick={async () => {
                              try {
                                const fullProduct = await getProductById(item.id);
                                setSelectedProduct(fullProduct.data);
                                setModalOpen(true);
                              } catch (error) {
                                console.error('Failed to fetch product details:', error);
                              }
                            }}
                          />
                        </div>
                        <div className="item-details" style={{ width: '100%', textAlign: 'center' }}>
                          <div className="item-header">
                            <h4 
                              className="item-title"
                              onClick={async () => {
                                try {
                                  const fullProduct = await getProductById(item.id);
                                  setSelectedProduct(fullProduct.data);
                                  setModalOpen(true);
                                } catch (error) {
                                  console.error('Failed to fetch product details:', error);
                                }
                              }}
                            >
                              {item.title || item.name}
                            </h4>
                            <button 
                              className="item-remove"
                              onClick={() => removeItem(item)}
                            >
                              <AiOutlineDelete />
                            </button>
                          </div>
                          
                          {(item.selectedColor || item.selectedSize) && (
                            <div className="item-variants">
                              {item.selectedColor && (
                                <span className="variant-tag color-tag">
                                  {item.selectedColor}
                                </span>
                              )}
                              {item.selectedSize && (
                                <span className="variant-tag size-tag">
                                  {item.selectedSize}
                                </span>
                              )}
                            </div>
                          )}

                          <div className="item-price-controls">
                            <div className="quantity-control">
                              <button 
                                onClick={() => updateQuantityHandler(item.id, item.quantity - 1)}
                                className="qty-btn minus"
                              >
                                <MdRemoveCircle />
                              </button>
                              <span className="qty-value">{item.quantity}</span>
                              <button 
                                onClick={() => updateQuantityHandler(item.id, item.quantity + 1)}
                                className="qty-btn plus"
                              >
                                <IoIosAddCircle />
                              </button>
                            </div>
                            <div className="item-pricing">
                              <span className="unit-price">{formatCurrency(item.price)}</span>
                              <span className="item-total">{formatCurrency(item.price * item.quantity)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Total Summary */}
                  <div className="order-total-summary">
                    <div className="summary-row">
                      <span>Subtotal ({displayItemCount} items)</span>
                      <span>{formatCurrency(cartTotal)}</span>
                    </div>
                    <div className="summary-row">
                      <span>Shipping</span>
                      <span className="free-shipping">FREE</span>
                    </div>
                    <div className="summary-row tax-row">
                      <span>Tax</span>
                      <span>₹0.00</span>
                    </div>
                    <div className="summary-divider"></div>
                    <div className="summary-row total-row">
                      <span>Total Amount</span>
                      <span className="total-amount">{formatCurrency(cartTotal)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right Column - Address & Payment */}
            <div className="checkout-process-panel">
              {isLoading ? (
                <div className="loading-container">
                  <Spinner />
                  <p>Loading your information...</p>
                </div>
              ) : (
                <>
                  {activeStep === 1 ? (
                    <div className="address-section">
                      <div className="section-header">
                        <h3><FaMapMarkerAlt /> Shipping Address</h3>
                        {addresses.length > 0 && !showAddressForm && (
                          <button 
                            className="btn-add-new"
                            onClick={() => { setAddressFormData({}); setAddressStep('pincode'); setShowAddressForm(true); }}
                          >
                            <FaPlusCircle /> Add New
                          </button>
                        )}
                      </div>

                      {!showAddressForm ? (
                        <>
                          {addresses.length > 0 ? (
                            <div className="address-selection">
                              {addresses.map(addr => (
                                <div 
                                  key={addr._id} 
                                  className={`address-card ${selectedAddress?._id === addr._id ? 'selected' : ''}`}
                                  onClick={() => setSelectedAddress(addr)}
                                >
                                  <div className="address-radio">
                                    <div className={`radio-circle ${selectedAddress?._id === addr._id ? 'checked' : ''}`}>
                                      {selectedAddress?._id === addr._id && <FaCheckCircle />}
                                    </div>
                                  </div>
                                  <div className="address-content">
                                    <div className="address-header">
                                      <h4>{addr.is_default && <span className="default-badge">Default</span>}</h4>
                                      <button 
                                        className="edit-address-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAddressFormData({
                                            _id: addr._id,
                                            addressLine1: addr.street || addr.addressLine1 || '',
                                            city: addr.city || '',
                                            state: addr.state || '',
                                            pincode: addr.pincode || '',
                                            country: addr.country || 'India',
                                            phone: addr.phone || '',
                                            is_default: !!addr.is_default
                                          });
                                          setAddressStep('details');
                                          setShowAddressForm(true);
                                        }}
                                      >
                                        <FaRegEdit />
                                      </button>
                                    </div>
                                    <p className="address-text">
                                      {addr.street || addr.addressLine1}<br />
                                      {addr.city}, {addr.state} - {addr.pincode}<br />
                                      {addr.country}<br />
                                      <strong>Phone:</strong> {addr.phone}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="no-address">
                              <p>No addresses found. Please add a shipping address.</p>
                              <button 
                                className="btn-primary"
                                onClick={() => { setAddressFormData({}); setAddressStep('pincode'); setShowAddressForm(true); }}
                              >
                                <FaPlusCircle /> Add Address
                              </button>
                            </div>
                          )}

                          <div className="terms-checkbox">
                            <label className="checkbox-container">
                              <input 
                                type="checkbox" 
                                checked={termsAccepted} 
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                              />
                              <span className="checkmark"></span>
                              I have read and agree to the <Link to="/terms-and-conditions" target="_blank">Terms & Conditions</Link> and <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>.
                            </label>
                          </div>

                          <button 
                            className="btn-primary proceed-btn"
                            onClick={() => {
                              handleProceedToPayment();
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            disabled={!selectedAddress || cart.items.length === 0 || !termsAccepted}
                          >
                            Continue to Payment
                            <MdOutlineKeyboardArrowRight />
                          </button>
                        </>
                      ) : (
                        <div className="address-form-container">
                          {addressStep === 'pincode' ? (
                            <div className="pincode-step">
                              <div className="step-back">
                                <button 
                                  className="back-btn"
                                  onClick={() => setShowAddressForm(false)}
                                >
                                  <IoIosArrowBack /> Back
                                </button>
                              </div>
                              <h3>Enter Pincode</h3>
                              <p className="step-description">Enter your pincode to auto-fill address details</p>
                              <div className="pincode-input-group">
                                <input 
                                  type="text" 
                                  className="form-input"
                                  name="pincode" 
                                  value={addressFormData.pincode || ''} 
                                  onChange={handleAddressFormChange}
                                  placeholder="Enter 6-digit pincode"
                                  maxLength={6}
                                />
                                <button 
                                  className="btn-primary"
                                  onClick={() => fetchPincodeAndFill(addressFormData.pincode)}
                                  disabled={isFetchingPin}
                                >
                                  {isFetchingPin ? 'Fetching...' : 'Continue'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="address-form-step">
                              <div className="step-back">
                                <button 
                                  className="back-btn"
                                  onClick={() => setShowAddressForm(false)}
                                >
                                  <IoIosArrowBack /> Back
                                </button>
                              </div>
                              <h3>{addressFormData._id ? 'Edit Address' : 'Add New Address'}</h3>
                              <form onSubmit={handleSaveAddress}>
                                <div className="form-grid">
                                  <div className="form-group full-width">
                                    <label>Address Line 1 *</label>
                                    <input 
                                      type="text" 
                                      className="form-input"
                                      name="addressLine1" 
                                      value={addressFormData.addressLine1 || ''} 
                                      onChange={handleAddressFormChange}
                                      required
                                      placeholder="House/Flat number, Street"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>City *</label>
                                    <input 
                                      type="text" 
                                      className="form-input"
                                      name="city" 
                                      value={addressFormData.city || ''} 
                                      onChange={handleAddressFormChange}
                                      required
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>State *</label>
                                    <input 
                                      type="text" 
                                      className="form-input"
                                      name="state" 
                                      value={addressFormData.state || ''} 
                                      onChange={handleAddressFormChange}
                                      required
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Pincode *</label>
                                    <input 
                                      type="text" 
                                      className="form-input"
                                      name="pincode" 
                                      value={addressFormData.pincode || ''} 
                                      onChange={handleAddressFormChange}
                                      required
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Country *</label>
                                    <input 
                                      type="text" 
                                      className="form-input"
                                      name="country" 
                                      value={addressFormData.country || 'India'} 
                                      onChange={handleAddressFormChange}
                                      required
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Phone Number *</label>
                                    <input 
                                      type="tel" 
                                      className="form-input"
                                      name="phone" 
                                      value={addressFormData.phone || ''} 
                                      onChange={handleAddressFormChange}
                                      required
                                    />
                                  </div>
                                </div>
                                <div className="form-actions">
                                  <button 
                                    type="submit"
                                    className="btn-primary save-btn"
                                    disabled={isProcessing}
                                  >
                                    {isProcessing ? 'Saving...' : 'Save Address'}
                                  </button>
                                  <button 
                                    type="button"
                                    className="btn-secondary cancel-btn"
                                    onClick={() => setShowAddressForm(false)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="payment-section">
                      <div className="section-header">
                        <button 
                          className="back-to-address"
                          onClick={() => {
                            setActiveStep(1);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          style={{
                            backgroundColor: '#82278A',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontWeight: '500'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#6a1c70'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#82278A'}
                        >
                          <IoIosArrowBack /> Back to Address
                        </button>
                        <h3>Payment</h3>
                      </div>

                      <div className="selected-address-preview">
                        <div className="address-preview-header">
                          <FaMapMarkerAlt />
                          <span>Delivering to:</span>
                        </div>
                        <div className="address-preview-content">
                          <p>{selectedAddress?.street || selectedAddress?.addressLine1}</p>
                          <p>{selectedAddress?.city}, {selectedAddress?.state} - {selectedAddress?.pincode}</p>
                          <p>Phone: {selectedAddress?.phone}</p>
                        </div>
                      </div>

                      <div className="payment-methods">
                        <h4>Select Payment Method</h4>
                        <div className="payment-options">
                          <div className="payment-option selected">
                            <div className="payment-radio">
                              <div className="radio-circle checked">
                                <FaCheckCircle />
                              </div>
                            </div>
                            <div className="payment-content">
                              <div className="payment-header">
                                <h5>Razorpay</h5>
                                <FaCreditCard style={{ fontSize: '28px', color: '#82287A' }} />
                              </div>
                              <p className="payment-description">Pay securely using UPI, Credit/Debit Card, Net Banking</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="payment-summary">
                        <div className="payment-total">
                          <span>Total Amount</span>
                          <span className="total-amount">{formatCurrency(cartTotal)}</span>
                        </div>
                        <button 
                          className="btn-primary pay-now-btn"
                          onClick={handlePayment}
                          disabled={isProcessing || cart.items.length === 0}
                        >
                          {isProcessing ? 'Processing...' : `Pay ${formatCurrency(cartTotal)}`}
                        </button>
                        <p className="secure-payment-note">
                          <FaCheckCircle /> Secure payment powered by Razorpay
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <ProductModal 
        post={selectedProduct || {}} 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        initialSelectedColor={selectedProduct?.selectedColor} 
        initialSelectedSize={selectedProduct?.selectedSize} 
        isWishlistPage={false} 
      />

      {/* Remove Item Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '2px solid #82278A' }}>
          <Modal.Title style={{ color: '#82278A' }}>Confirm Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: 'center', padding: '30px', fontSize: '16px' }}>
          Are you sure you want to remove this item from your cart?
          {itemToRemove && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <strong>{itemToRemove.title}</strong>
              {itemToRemove.selectedColor && itemToRemove.selectedSize && (
                <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                  {itemToRemove.selectedColor} / {itemToRemove.selectedSize}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="confirm-actions" style={{ borderTop: 'none' }}>
          <Button variant="secondary" onClick={() => setShowConfirm(false)} className="confirm-btn cancel-btn">
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmRemove} className="confirm-btn" style={{ backgroundColor: '#82278A', borderColor: '#82278A'}}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CheckoutPage;